import { useState, useRef, useEffect } from "react";
import { askChatbot } from "../../services/Chatbot_service";
import "./Chatbot.css";

const SUGGESTED_QUESTIONS = [
  "How many vehicles do we have?",
  "How many drivers do we have?",
  "List all active suppliers",
  "Show me all vehicles",
];
let idCounter = 0;
const nextId = () => `msg-${++idCounter}-${Date.now()}`;

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isLoading, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const sendQuestion = async (question) => {
    const trimmed = question.trim();
    if (!trimmed || isLoading) return;

    const userMsg = { id: nextId(), role: "user", text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);

    try {
      const data = await askChatbot(trimmed);
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: "bot",
          text: data.answer,
          sql: data.sql_used || null,
          rowCount: data.row_count ?? null,
        },
      ]);
      if (!isOpen) setHasUnread(true);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: "bot", text: err.message || "Something went wrong. Please try again.", isError: true },
      ]);
      if (!isOpen) setHasUnread(true);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuestion(input);
  };

  return (
    <div className="fchat-root">
      {isOpen && (
        <div className="fchat-panel" role="dialog" aria-label="Fleet Data Assistant">
          <header className="fchat-header">
            <div>
              <h1>Fleet Data Assistant</h1>
              <p>Ask about drivers, vehicles, suppliers, or assignments.</p>
            </div>
            <button
              type="button"
              className="fchat-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <CloseIcon />
            </button>
          </header>

          <div className="fchat-log" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="fchat-empty">
                <p className="fchat-empty-title">Ask anything about your fleet data</p>
                <div className="fchat-suggestions">
                  {SUGGESTED_QUESTIONS.map((q) => (
                    <button key={q} className="fchat-chip" onClick={() => sendQuestion(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <ChatMessage key={msg.id} msg={msg} />
            ))}

            {isLoading && (
              <div className="fchat-row bot">
                <div className="fchat-bubble bot typing">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
              </div>
            )}
          </div>

          <form className="fchat-input-bar" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. Which drivers have licenses expiring in 30 days?"
              disabled={isLoading}
              autoComplete="off"
            />
            <button type="submit" disabled={isLoading || !input.trim()} aria-label="Send message">
              <SendIcon />
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        className="fchat-launcher"
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isOpen ? "Close Fleet Data Assistant" : "Open Fleet Data Assistant"}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
        {!isOpen && hasUnread && <span className="fchat-unread-dot" />}
      </button>
    </div>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 5.5C4 4.67 4.67 4 5.5 4h13c.83 0 1.5.67 1.5 1.5v10c0 .83-.67 1.5-1.5 1.5H9l-4 3.5v-3.5H5.5C4.67 17 4 16.33 4 15.5v-10Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 12l16-8-6 8 6 8-16-8Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
        fill="currentColor"
      />
    </svg>
  );
}

function ChatMessage({ msg }) {
  if (msg.role === "user") {
    return (
      <div className="fchat-row user">
        <div className="fchat-bubble user">{msg.text}</div>
      </div>
    );
  }

  return (
    <div className="fchat-row bot">
      <div className={`fchat-bubble bot ${msg.isError ? "error" : ""}`}>
        <p className="fchat-text">{msg.text}</p>
      </div>
    </div>
  );
}
